import React from "react";
import dayjs from "dayjs";
import { AlertTriangle, Clock } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

function urgencyStyle(due) {
  if (!due) return { color: "text-white/30", bg: "bg-white/5 border-white/10", days: null };
  const days = dayjs(due).diff(dayjs(), "day");
  if (days < 0)  return { color: "text-red-400",    bg: "bg-red-500/10 border-red-500/20",       days, label: "Overdue"     };
  if (days <= 7) return { color: "text-red-400",    bg: "bg-red-500/10 border-red-500/20",       days, label: `${days}d`   };
  if (days <= 14)return { color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/20", days, label: `${days}d`   };
  if (days <= 30)return { color: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/20", days, label: `${days}d`   };
  return           { color: "text-white/30",    bg: "bg-white/5 border-white/10",            days, label: `${days}d`   };
}

const TYPE_ICONS = {
  "Contract Expiry":   "📄",
  "Training Overdue":  "🎓",
  "Policy Sign-off":   "✍️",
  "Document Missing":  "📋",
  "Probation Review":  "⏱️",
};

export default function CompliancePanel({ loading, data }) {
  const alerts = data?.alerts || [];

  // Sort by urgency
  const sorted = [...alerts].sort((a, b) => {
    const da = a.due ? dayjs(a.due).diff(dayjs(), "day") : 999;
    const db = b.due ? dayjs(b.due).diff(dayjs(), "day") : 999;
    return da - db;
  });

  const critical = sorted.filter(a => {
    const days = a.due ? dayjs(a.due).diff(dayjs(), "day") : 999;
    return days <= 7;
  }).length;

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Compliance & Risk</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Compliance & Risk</span>
          {critical > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">
              <AlertTriangle size={10} /> {critical} urgent
            </span>
          )}
        </div>
        <span className="text-xs text-white/30">{alerts.length} alerts</span>
      </div>

      <div className="p-4 space-y-2">
        {sorted.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-4">No compliance alerts.</p>
        ) : (
          sorted.map(a => {
            const u = urgencyStyle(a.due);
            const icon = TYPE_ICONS[a.type] || "⚠️";
            return (
              <div key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/5 bg-white/3 px-4 py-3">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="text-base shrink-0 mt-0.5">{icon}</span>
                  <div className="min-w-0">
                    <p className="text-sm text-white/70 font-medium">{a.type}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {a.count && <span className="text-xs text-white/30">{a.count} affected</span>}
                      {a.due && (
                        <div className="flex items-center gap-1 text-xs text-white/25">
                          <Clock size={10} /> {dayjs(a.due).format("DD MMM YYYY")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {u.label && (
                  <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${u.bg} ${u.color}`}>
                    {u.label}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}